import { useState, useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";
import { fillPDFBytes, download1099PDF } from "../components/Form1099PDF";

const API_URL = process.env.REACT_APP_API_URL;

// ── Formatters ────────────────────────────────────────────────────────────────

const fmtAmount = (n) => {
  const num = parseFloat(n);
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPhone = (val) => {
  let d = val.replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const formatEIN = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}-${d.slice(2)}`;
};

const formatSSN = (val) => {
  const d = val.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const formatZip = (val) => val.replace(/\D/g, "").slice(0, 5);

// ── Constants ─────────────────────────────────────────────────────────────────

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC","PR","GU","VI","AS","MP",
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const BLANK = {
  taxYear: String(currentYear - 1),
  payerLine1: "",
  payerLine2: "",
  payerCity: "",
  payerState: "",
  payerZip: "",
  payerPhone: "",
  payerTIN: "",
  recipientName: "",
  recipientAddress: "",
  recipientCity: "",
  recipientState: "",
  recipientZip: "",
  recipientTIN: "",
  box1: "",
  box4: "",
};

/** Reconstructs payerLine3 and recipientCityLine for the PDF fill functions. */
const toFormData = (f) => ({
  ...f,
  payerLine3: [
    [f.payerCity, f.payerState].filter(Boolean).join(", "),
    f.payerZip,
    f.payerPhone,
  ].filter(Boolean).join("  "),
  recipientCityLine: [
    [f.recipientCity, f.recipientState].filter(Boolean).join(", "),
    f.recipientZip,
  ].filter(Boolean).join("  "),
});

/** Strips formatting characters from an amount string to get a float. */
const parseAmount = (s) => parseFloat((s || "").replace(/,/g, "")) || 0;

// ── Component ─────────────────────────────────────────────────────────────────

const Generate1099 = () => {
  const auth = useAuth();
  const { t } = useTranslation();
  const { activeCompany, isDemo } = useCompany();

  const [employees, setEmployees] = useState([]);
  const [annualTotals, setAnnualTotals] = useState({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // "saving" | "saved" | "error"

  const [form, setForm] = useState(BLANK);

  // ── Live PDF preview state ──
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef(null);
  const prevUrlRef = useRef(null);

  // ── Load employees + annual totals when year or company changes ──
  useEffect(() => {
    if (!auth?.isAuthenticated || !activeCompany) return;

    const token = auth.user?.access_token;
    const companyId = activeCompany.company_id;
    const headers = { Authorization: `Bearer ${token}` };

    setLoadingData(true);
    setSelectedEmployeeId("");

    Promise.all([
      fetch(`${API_URL}/employees?company_id=${companyId}`, { headers })
        .then((r) => (r.ok ? r.json() : [])).catch(() => []),
      fetch(`${API_URL}/payroll_entries/annual_totals?year=${form.taxYear}&company_id=${companyId}`, { headers })
        .then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]).then(([emps, totals]) => {
      setEmployees(emps);
      const map = {};
      totals.forEach((row) => { map[row.employee_id] = row.annual_total; });
      setAnnualTotals(map);

      setForm((prev) => ({
        ...prev,
        payerLine1: activeCompany.name    || "",
        payerLine2: activeCompany.address || "",
        payerCity:  activeCompany.city    || "",
        payerState: activeCompany.state   || "",
        payerZip:   formatZip(activeCompany.zip   || ""),
        payerPhone: formatPhone(activeCompany.phone || ""),
        // The company's EIN when it has one (not carried over from another company).
        payerTIN:   activeCompany.ein ? formatEIN(activeCompany.ein) : prev.payerTIN,
      }));

      setLoadingData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, activeCompany, form.taxYear]);

  // ── Auto-fill recipient fields when employee is selected ──
  useEffect(() => {
    if (!selectedEmployeeId) return;
    const emp = employees.find((e) => e.employee_id === selectedEmployeeId);
    if (!emp) return;

    const total = annualTotals[selectedEmployeeId] || 0;
    setForm((prev) => ({
      ...prev,
      recipientName:    `${emp.first_name} ${emp.last_name}`,
      recipientAddress: emp.address || "",
      recipientCity:    "",
      recipientState:   "",
      recipientZip:     "",
      recipientTIN:     "",
      box1: fmtAmount(total),
    }));
  }, [selectedEmployeeId, employees, annualTotals]);

  // ── Debounced live PDF preview ──
  useEffect(() => {
    clearTimeout(debounceRef.current);
    setPreviewLoading(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const bytes = await fillPDFBytes(toFormData(form));
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        console.error("Preview failed:", err);
      } finally {
        setPreviewLoading(false);
      }
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [form]);

  // ── Revoke blob URL on unmount ──
  useEffect(() => {
    return () => { if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current); };
  }, []);

  // ── Setters ──────────────────────────────────────────────────────────────────

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const setFmt = (key, formatter) => (e) =>
    setForm((prev) => ({ ...prev, [key]: formatter(e.target.value) }));

  // ── Download + save to DB ──────────────────────────────────────────────────

  const handleDownload = async () => {
    setDownloading(true);
    setSaveStatus("saving");
    try {
      await download1099PDF(toFormData(form));

      // Upsert this filing to the DB
      const token = auth.user?.access_token;
      const res = await fetch(`${API_URL}/filings_1099`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id:       activeCompany.company_id,
          employee_id:      selectedEmployeeId || "manual",
          tax_year:         form.taxYear,
          payer_line1:      form.payerLine1,
          payer_line2:      form.payerLine2,
          payer_city:       form.payerCity,
          payer_state:      form.payerState,
          payer_zip:        form.payerZip,
          payer_phone:      form.payerPhone,
          payer_tin:        form.payerTIN,
          recipient_name:   form.recipientName,
          recipient_address: form.recipientAddress,
          recipient_city:   form.recipientCity,
          recipient_state:  form.recipientState,
          recipient_zip:    form.recipientZip,
          box1: parseAmount(form.box1),
          box4: parseAmount(form.box4),
        }),
      });
      setSaveStatus(res.ok ? "saved" : "error");
    } catch {
      setSaveStatus("error");
    } finally {
      setDownloading(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const selectedEmp = employees.find((e) => e.employee_id === selectedEmployeeId);
  const isEligible = selectedEmp ? (annualTotals[selectedEmp.employee_id] || 0) >= 600 : false;

  // ── Styles ───────────────────────────────────────────────────────────────────

  // Always-interactive class (year selector, employee picker)
  const selectCls = "w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white";
  // Locked in demo mode
  const inputCls = isDemo
    ? "w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-gray-100 text-gray-400 cursor-not-allowed"
    : selectCls;
  const labelCls = "block text-xs font-medium text-gray-500 mb-1";

  const StateSelect = ({ value, onChange, disabled }) => (
    <select value={value} onChange={onChange} className={inputCls} disabled={disabled}>
      <option value="">—</option>
      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );

  const Spinner = () => (
    <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
      <svg className="animate-spin h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      Loading form…
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
        <div className="flex flex-col flex-1 p-4 gap-4">

          <div>
            <h1 className="text-xl font-semibold text-gray-800">{t("generate_1099_title")}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{t("generate_1099_subtitle")}</p>
          </div>

          <div className="flex gap-4 items-start flex-col xl:flex-row">

            {/* ── LEFT PANEL: FORM ── */}
            <div className="bg-white shadow rounded-lg p-5 w-full xl:w-80 flex-shrink-0 space-y-5">

              {/* 1 · Year & Recipient */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">1 · Year &amp; Recipient</h2>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>{t("tax_year_label")}</label>
                    <select value={form.taxYear} onChange={set("taxYear")} className={selectCls}>
                      {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>
                      {t("col_employee")}
                      {loadingData && <span className="ml-2 text-gray-400">(loading…)</span>}
                    </label>
                    <select
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      className={selectCls}
                      disabled={loadingData || employees.length === 0}
                    >
                      <option value="">— Select employee —</option>
                      {employees.map((emp) => {
                        const total = annualTotals[emp.employee_id] || 0;
                        return (
                          <option key={emp.employee_id} value={emp.employee_id}>
                            {emp.first_name} {emp.last_name}{total >= 600 ? " ✓" : " (< $600)"}
                          </option>
                        );
                      })}
                    </select>
                    {selectedEmp && (
                      <p className={`text-xs mt-1 ${isEligible ? "text-green-600" : "text-amber-600"}`}>
                        {isEligible ? "1099-NEC required (≥ $600)" : t("below_threshold_note")}
                      </p>
                    )}
                  </div>
                </div>
              </section>

              {isDemo && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                  <span className="font-semibold">Demo mode —</span> fields are pre-filled with sample data and cannot be edited. Subscribe to use your own information.
                </div>
              )}

              <hr className="border-gray-100" />

              {/* 2 · Payer Information */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">2 · Payer Information</h2>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Business Name</label>
                    <input className={inputCls} value={form.payerLine1} onChange={set("payerLine1")} placeholder="Your Business Name" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>Street Address</label>
                    <input className={inputCls} value={form.payerLine2} onChange={set("payerLine2")} placeholder="123 Main St" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input className={inputCls} value={form.payerCity} onChange={set("payerCity")} placeholder="Miami" disabled={isDemo} />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-20 flex-shrink-0">
                      <label className={labelCls}>State</label>
                      <StateSelect value={form.payerState} onChange={set("payerState")} disabled={isDemo} />
                    </div>
                    <div className="flex-1">
                      <label className={labelCls}>ZIP</label>
                      <input
                        className={inputCls}
                        value={form.payerZip}
                        onChange={setFmt("payerZip", formatZip)}
                        placeholder="33101"
                        inputMode="numeric"
                        maxLength={5}
                        disabled={isDemo}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <div className={`flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white ${isDemo ? "opacity-60" : "focus-within:ring-2 focus-within:ring-green-400"}`}>
                      <span className="px-2.5 text-sm text-gray-400 border-r border-gray-200 bg-gray-50 select-none">+1</span>
                      <input
                        className={`flex-1 px-3 py-1.5 text-sm focus:outline-none ${isDemo ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white"}`}
                        value={form.payerPhone}
                        onChange={setFmt("payerPhone", formatPhone)}
                        placeholder="(305) 555-0000"
                        inputMode="tel"
                        disabled={isDemo}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t("payer_ein_label")}</label>
                    <input
                      className={inputCls}
                      value={form.payerTIN}
                      onChange={setFmt("payerTIN", formatEIN)}
                      placeholder="12-3456789"
                      inputMode="numeric"
                      disabled={isDemo}
                    />
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* 3 · Recipient Information */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">3 · Recipient Information</h2>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Full Name</label>
                    <input className={inputCls} value={form.recipientName} onChange={set("recipientName")} placeholder="John Doe" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>Street Address</label>
                    <input className={inputCls} value={form.recipientAddress} onChange={set("recipientAddress")} placeholder="456 Elm St" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <input className={inputCls} value={form.recipientCity} onChange={set("recipientCity")} placeholder="Miami" disabled={isDemo} />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-20 flex-shrink-0">
                      <label className={labelCls}>State</label>
                      <StateSelect value={form.recipientState} onChange={set("recipientState")} disabled={isDemo} />
                    </div>
                    <div className="flex-1">
                      <label className={labelCls}>ZIP</label>
                      <input
                        className={inputCls}
                        value={form.recipientZip}
                        onChange={setFmt("recipientZip", formatZip)}
                        placeholder="33101"
                        inputMode="numeric"
                        maxLength={5}
                        disabled={isDemo}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>{t("recipient_tin_label")}</label>
                    <input
                      className={inputCls}
                      value={form.recipientTIN}
                      onChange={setFmt("recipientTIN", formatSSN)}
                      placeholder="123-45-6789"
                      inputMode="numeric"
                      disabled={isDemo}
                    />
                    <p className="text-xs text-gray-400 mt-1">{t("tin_not_stored_note")}</p>
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* 4 · Compensation */}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">4 · Compensation</h2>
                <div className="space-y-2">
                  <div>
                    <label className={labelCls}>Box 1 — Nonemployee Compensation</label>
                    <input className={inputCls} value={form.box1} onChange={set("box1")} placeholder="0.00" disabled={isDemo} />
                  </div>
                  <div>
                    <label className={labelCls}>Box 4 — Federal Tax Withheld</label>
                    <input className={inputCls} value={form.box4} onChange={set("box4")} placeholder="0.00" disabled={isDemo} />
                  </div>
                </div>
              </section>

              <hr className="border-gray-100" />

              <button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition"
              >
                {downloading ? t("generating") : `↓ ${t("generate_pdf_btn")}`}
              </button>

              {saveStatus === "saved" && (
                <p className="text-xs text-green-600 text-center">Filing saved to records.</p>
              )}
              {saveStatus === "error" && (
                <p className="text-xs text-red-500 text-center">Could not save filing — check connection.</p>
              )}

            </div>

            {/* ── RIGHT PANEL: LIVE PDF PREVIEW ── */}
            <div className="flex-1 min-w-0">
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Live Preview — Form 1099-NEC
                  </span>
                  <span className={`text-xs ${previewLoading ? "text-amber-500" : "text-gray-400"}`}>
                    {previewLoading ? "Updating…" : "Up to date"}
                  </span>
                </div>
                {previewUrl ? (
                  <iframe
                    key={previewUrl}
                    src={`${previewUrl}#page=3`}
                    title="1099-NEC Preview"
                    className="w-full"
                    style={{ height: "900px", border: "none", display: "block" }}
                  />
                ) : (
                  <Spinner />
                )}
              </div>
            </div>

          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default Generate1099;
